/**
 * Which tenant the app is currently looking at, and what this user may do in it.
 *
 * Multi-tenancy is not a route parameter here. It could have been
 * (`/t/$tenantId/...`), and that is the usual shape — but almost every user of
 * this app belongs to exactly one tenant, and paying for a path segment and a
 * loader guard on every route to serve the rare second one is the wrong trade.
 * The active tenant is app state, persisted, with a switcher in the header.
 *
 * The consequence to remember: a query key that does not include the tenant id
 * is a cache entry two tenants share. Every `*QueryOptions` factory in
 * `src/api/` takes `tenantId` as its first argument for exactly this reason.
 */
import {
  displayNameOf,
  emailNameOf,
  GrowthView,
  type Role,
  type UserProfile,
  paths,
  VIEW_SCOPES,
  type ViewScope,
} from '@puppy-doctor/platform';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { createContext, type ReactNode, use, useEffect, useMemo, useRef, useState } from 'react';

import { setProfileGrowthView } from '@/api/mutations.ts';
import { useAuth } from '@/auth/AuthProvider.tsx';
import { db } from '@/firebase.ts';
import { adoptLocale, currentLocale } from '@/i18n/index.ts';

const STORAGE_KEY = 'puppy-doctor:active-tenant';

/**
 * The signed-in person, as the app draws them.
 *
 * `name` is what goes on screen and is never empty for a signed-in user;
 * `chosen` is what they typed, which is empty for anyone who did not type
 * anything, and is what the editor on the people screen is seeded from. Both,
 * because seeding the editor with the fallback would turn "I gave no name" into
 * "my name happens to be the front of my email address" the first time the field
 * lost focus.
 */
type Profile = {
  /** The name to render. Falls back to the local part of the address. */
  name: string;
  /** The name this person set for themselves. Empty if they never did. */
  chosen: string;
  /** What `name` falls back to, shown as the editor's placeholder. */
  fallback: string;
  email: string;
  /**
   * Chart or table, wherever growth is drawn. `chart` for anyone who has never
   * chosen — including every profile written before the growth section existed.
   */
  growthView: GrowthView;
};

type TenantContextValue = {
  /** null while loading, or when this user belongs to no tenant yet. */
  tenantId: string | null;
  tenantIds: readonly string[];
  role: Role | null;
  /**
   * Which parts of a litter's record this person may read — see `VIEW_SCOPES` in
   * platform/src/roles.ts. Only a `viewer` is scoped by it; ask `canView` rather
   * than reading this list directly, so an owner whose membership happens to
   * carry a short list is not hidden from their own kennel.
   *
   * All of them for a membership written before scopes existed, which is what
   * the converter and the security rules both read an absent field as.
   */
  scopes: readonly ViewScope[];
  setTenantId: (id: string) => void;
  /**
   * Remembers how this person reads growth, across their devices. Rides along on
   * the profile listener for the same reason the name does — one document, one
   * subscription. See `setProfileGrowthView`.
   */
  setGrowthView: (view: GrowthView) => void;
  loading: boolean;
  /**
   * Rides along here because the user-profile listener is already open below —
   * the document that carries `tenantIds` is the document that carries the name.
   * A second provider for it would be a second subscription to the same doc.
   */
  profile: Profile;
};

const TenantContext = createContext<TenantContextValue | null>(null);

export const TenantProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [subscribed, setProfile] = useState<Pick<
    UserProfile,
    'tenantIds' | 'displayName' | 'email' | 'growthView'
  > | null>(null);
  const [active, setActive] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY) ?? null,
  );
  const [subscribedRole, setRole] = useState<Role | null>(null);
  const [subscribedScopes, setScopes] = useState<readonly ViewScope[]>(VIEW_SCOPES);

  /**
   * Signing out clears these by DERIVING them from `user`, not by calling
   * setState from an effect. Both spellings work; only this one avoids a
   * cascading render, and only this one cannot leave the previous account's
   * tenant list on screen for a frame — which on a shared tablet is a real leak,
   * not a cosmetic one.
   */
  const profile = user === null ? null : subscribed;
  const role = user === null ? null : subscribedRole;
  const scopes = user === null ? VIEW_SCOPES : subscribedScopes;

  /**
   * At most one profile write per session, and the reason is a loop that has
   * already happened once.
   *
   * A rejected write is rolled back locally, and the rollback is itself a
   * snapshot — the document reverts to "does not exist", the listener fires
   * again, and the same doomed write goes out again, for as long as the tab is
   * open. Every few seconds, forever, in the console.
   *
   * Retrying cannot help in any case. An offline write does not fail, it
   * queues; a write that actually fails here failed on permissions or a
   * precondition, and the hundredth attempt is denied for the reason the first
   * one was. So it is attempted once, and the failure is reported once.
   */
  const attemptedFor = useRef<string | null>(null);

  // The user document carries `tenantIds`, denormalised by the membership
  // function. It is the only thing a signed-in user can read before they are a
  // member of anything, which is what makes the empty state reachable at all.
  //
  // It is also written here, the first time this person is seen. That happens
  // in the tenant provider and NOT in the auth provider on purpose: a failed
  // write here leaves a signed-in user looking at an empty app, which they can
  // recover from; the same failure during sign-in leaves them with no session
  // and nothing to recover with. See AuthProvider's header comment.
  useEffect(() => {
    if (user === null) return;
    return onSnapshot(doc(db, paths.user(user.uid)), (snap) => {
      const data = snap.data();
      setProfile({
        tenantIds: Array.isArray(data?.tenantIds) ? (data.tenantIds as string[]) : [],
        // Both may be missing: the membership function creates this document
        // with nothing in it but `tenantIds` when someone is invited before they
        // have ever opened the app.
        displayName: typeof data?.displayName === 'string' ? data.displayName : '',
        email: typeof data?.email === 'string' ? data.email : '',
        // Absent from every profile written before the growth section existed,
        // and read straight from the document like `locale` — no converter runs
        // on this one, which is why every field here is narrowed by hand.
        growthView: GrowthView.safeParse(data?.growthView).data ?? 'chart',
      });

      // The language this person chose, wherever they chose it. Read from the
      // same snapshot rather than a second listener because it is the same
      // document, and applied rather than stored in state because i18next owns
      // the current language and a second copy of it in React would be a second
      // thing to keep in step. `adoptLocale` yields to a choice made by hand in
      // this session — see i18n/index.ts.
      adoptLocale(data?.locale);

      // `fromCache` guards the create: offline, the first snapshot of a
      // document this device has never seen says "does not exist" whether or
      // not it does, and a create issued on that answer is rejected by the
      // rules the moment it syncs — `tenantIds: []` is not `unchanged`. Waiting
      // for the server to say so costs nothing, because the one thing that
      // needs the profile (creating a tenant) needs the server anyway.
      if (snap.metadata.fromCache) return;
      if (attemptedFor.current === user.uid) return;

      const fresh = !snap.exists();
      // A document the membership function created holds nothing but
      // `tenantIds`; `email` is the marker for "this has a person in it".
      if (!fresh && typeof data?.email === 'string') return;

      attemptedFor.current = user.uid;
      writeProfile(user, { fresh });
    });
  }, [user]);

  // A stored tenant id that this user is no longer a member of — they were
  // removed, or they signed in as someone else on a shared tablet — must not
  // survive. Falling back to the first available one is what stops the app
  // showing a permanent permission error with no way out.
  const tenantIds = useMemo(() => profile?.tenantIds ?? [], [profile]);
  const tenantId =
    active !== null && tenantIds.includes(active) ? active : (tenantIds[0] ?? null);

  useEffect(() => {
    if (tenantId !== null) localStorage.setItem(STORAGE_KEY, tenantId);
  }, [tenantId]);

  useEffect(() => {
    if (user === null || tenantId === null) return;
    return onSnapshot(doc(db, paths.member(tenantId, user.uid)), (snap) => {
      const data = snap.data();
      setRole((data?.role as Role | undefined) ?? null);
      // Narrowed by hand, like `growthView` above: no converter runs on this
      // snapshot. An absent field is every scope — the membership was written
      // before scopes existed, and the security rules default the same way, so
      // the screen and the rules cannot disagree about what is allowed.
      setScopes(
        Array.isArray(data?.scopes)
          ? (data.scopes as unknown[]).filter((one): one is ViewScope =>
              VIEW_SCOPES.includes(one as ViewScope),
            )
          : VIEW_SCOPES,
      );
    });
  }, [user, tenantId]);

  /**
   * The name, resolved once here so that every place that draws it draws the
   * same string.
   *
   * The session is the fallback for the document and not the other way round:
   * the profile is the source of truth, but it arrives from a listener a few
   * hundred milliseconds after the app has painted, and a header that shows the
   * front of an email address for that window and then swaps to a name reads as
   * two different people. Google's own name is the better first frame.
   *
   * `??` and not `||` on the chosen name: an empty string in the document is a
   * deliberate answer and must not fall back to what Google holds.
   */
  const people = useMemo<Profile>(() => {
    const email = profile?.email !== undefined && profile.email !== ''
      ? profile.email
      : (user?.email ?? '');
    const chosen = profile?.displayName ?? user?.displayName ?? '';

    return {
      name: displayNameOf({ displayName: chosen, email }),
      chosen: profile?.displayName ?? '',
      fallback: emailNameOf(email),
      email,
      growthView: profile?.growthView ?? 'chart',
    };
  }, [profile, user]);

  const value = useMemo<TenantContextValue>(
    () => ({
      tenantId,
      tenantIds,
      role,
      scopes,
      setTenantId: setActive,
      setGrowthView: (view: GrowthView) => {
        // Nothing local to set: the write is echoed back through the profile
        // listener above before the next frame, offline included. See the
        // offline write rule in api/mutations.ts.
        if (user !== null) setProfileGrowthView(user.uid, view);
      },
      loading: user !== null && profile === null,
      profile: people,
    }),
    [tenantId, tenantIds, role, scopes, user, profile, people],
  );

  return <TenantContext value={value}>{children}</TenantContext>;
};

/**
 * The person's own profile document, from the session that is already open.
 *
 * `tenantIds` is written exactly once, empty, and never again: the rules allow
 * `create` only with an empty array and `update` only when the field is
 * unchanged, because a client that could append to it could name a tenant it was
 * never invited to. The `fresh: false` branch exists for the document the
 * membership function may have created first with nothing in it but `tenantIds`
 * — merging without the field is what keeps that update legal.
 *
 * Not awaited: it is a Firestore write like any other, and offline it is queued
 * and durable. See api/mutations.ts.
 */
const writeProfile = (
  user: { uid: string; displayName: string | null; email: string | null; photoURL: string | null },
  { fresh }: { fresh: boolean },
): void => {
  const identity = {
    uid: user.uid,
    /**
     * The name the provider gave, and NOT the email address as a stand-in.
     * Writing the address here would make every account arrive with a display
     * name it never chose — one that reads as deliberate, is shown to everyone
     * else in the kennel, and is an address on a screen in a room with other
     * people in it. Empty is the honest value, and `displayNameOf` turns it into
     * the front of the address wherever a name is drawn.
     */
    displayName: user.displayName ?? '',
    email: user.email ?? '',
    photoUrl: user.photoURL,
    locale: currentLocale(),
    createdAt: Date.now(),
  };

  void setDoc(
    doc(db, paths.user(user.uid)),
    fresh ? { ...identity, tenantIds: [] } : identity,
    { merge: true },
  ).catch((error: unknown) => {
    // Reported once — see `attemptedFor` above for why it is not retried.
    //
    // `permission-denied` here almost always means the security rules in
    // production are older than this client: the rules are what name
    // `tenantIds`, and a ruleset from before that field was renamed denies the
    // write without saying so. `pnpm pd deploy rules indexes` is the fix.
    console.error(
      '[puppy-doctor] could not write the user profile. If this is permission-denied, ' +
        'the deployed security rules are probably older than this build — ' +
        'run `pnpm pd deploy rules indexes`.',
      error,
    );
  });
};

export const useTenant = (): TenantContextValue => {
  const value = use(TenantContext);
  if (value === null) throw new Error('useTenant outside TenantProvider');
  return value;
};

/**
 * What to call the signed-in person, and what they chose to be called.
 *
 * A hook of its own rather than reaching into `useTenant().profile` at every
 * call site: this is about the person, not the tenant, and the day the profile
 * gets its own listener this is the only thing that has to change.
 */
export const useProfile = (): Profile => useTenant().profile;

/**
 * How this person reads growth, and how to change it.
 *
 * A hook of its own so the growth section does not have to know that the
 * preference happens to live on the profile document: the day it moves, this is
 * the only thing that changes.
 */
export const useGrowthView = (): [GrowthView, (view: GrowthView) => void] => {
  const { profile, setGrowthView } = useTenant();
  return [profile.growthView, setGrowthView];
};

/**
 * What this person may see and do here, as the two questions every screen asks.
 *
 * A hook rather than `useTenant().role` plus `useTenant().scopes` at each call
 * site, because the pair is always asked together — a section is drawn when the
 * role allows it AND the scope does — and one of the two being read without the
 * other is how a screen ends up showing a viewer something nobody gave them.
 */
export const useAccess = (): { role: Role | null; scopes: readonly ViewScope[] } => {
  const { role, scopes } = useTenant();
  return { role, scopes };
};

/** The tenant id, for code that has already established there is one. */
export const useTenantId = (): string => {
  const { tenantId } = useTenant();
  if (tenantId === null) throw new Error('No active tenant — guard the route first.');
  return tenantId;
};
